import { useEffect, useState } from "react";
import EnergyQuizModal from "./EnergyQuizModal";

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <section className="relative w-full min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Tibetan%20dzi%20beads%20natural%20crystal%20jade%20bracelet%20energy%20jewelry%20on%20dark%20stone%20surface%20with%20soft%20golden%20light%20bokeh%20background%20luxury%20oriental%20aesthetic%20high%20end%20product%20photography%20warm%20amber%20tones%20misty%20atmosphere%20serene%20peaceful%20spiritual%20beauty&width=1920&height=1080&seq=hero001&orientation=landscape"
            alt="瓏靖手作能量 Hero 背景"
            className="w-full h-full object-cover object-center"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-16 pt-32 pb-24 flex flex-col justify-center min-h-screen">
          <div
            className={`transition-all duration-1000 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Tag */}
            <div className="inline-flex items-center gap-2 mb-8">
              <span className="w-8 h-px bg-[#c9a96e]"></span>
              <span
                className="text-[#c9a96e] text-[1rem] tracking-[0.3em] font-medium"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                東方能量美學
              </span>
              <span className="w-8 h-px bg-[#c9a96e]"></span>
            </div>

            {/* H1 */}
            <h1
              className="text-white font-bold leading-[1.3] mb-6 max-w-3xl"
              style={{
                fontSize: "clamp(2rem, 4vw, 2.75rem)",
                fontFamily: "'Noto Serif TC', serif",
                lineHeight: "1.3",
              }}
            >
              瓏靖手作能量
              <br />
              <span className="text-[#c9a96e]">匯聚天地能量</span>
              <br />
              守護你的身心靈
            </h1>

            {/* Subtitle */}
            <p
              className="text-white/80 max-w-xl mb-10 leading-[1.75]"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              嚴選西藏天珠、天然水晶礦石、翡翠玉石與唐卡佛牌，
              <br className="hidden md:block" />
              融合東方文化與能量信仰，
              <br className="hidden md:block" />
              為每一位有緣人帶來祝福與守護。
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button
                onClick={() => handleScroll("#products")}
                className="px-8 py-4 bg-[#8b6f47] text-white rounded-full text-[1rem] font-medium tracking-widest cursor-pointer hover:bg-[#7a5f3a] transition-all duration-300 whitespace-nowrap"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                探索能量系列
              </button>
              <button
                onClick={() => handleScroll("#featured")}
                className="px-8 py-4 bg-white/15 backdrop-blur-sm text-white border border-white/50 rounded-full text-[1rem] font-medium tracking-widest cursor-pointer hover:bg-white/25 transition-all duration-300 whitespace-nowrap"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                查看精選商品
              </button>
            </div>

            {/* Quiz Entry Card */}
            <div
              className={`transition-all duration-1000 delay-300 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <button
                onClick={() => setQuizOpen(true)}
                className="group inline-flex items-center gap-4 px-6 py-3.5 rounded-2xl border border-white/25 bg-white/10 backdrop-blur-md hover:bg-white/20 hover:border-[#c9a96e]/60 transition-all duration-300 cursor-pointer"
              >
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#c9a96e]/20 border border-[#c9a96e]/40 flex-shrink-0 group-hover:bg-[#c9a96e]/30 transition-colors duration-300">
                  <i className="ri-sparkling-2-line text-[#c9a96e] text-lg"></i>
                </div>

                {/* Text */}
                <div className="text-left">
                  <p
                    className="text-white text-sm font-semibold leading-tight"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    不知道選哪個？做個能量測驗吧
                  </p>
                  <p className="text-white/55 text-xs mt-0.5">
                    5 個問題 · 1 分鐘 · 精準推薦你的能量飾品
                  </p>
                </div>

                {/* Arrow */}
                <div className="ml-2 w-7 h-7 flex items-center justify-center rounded-full bg-[#c9a96e]/20 flex-shrink-0 group-hover:bg-[#c9a96e] transition-all duration-300">
                  <i className="ri-arrow-right-line text-[#c9a96e] text-sm group-hover:text-white transition-colors duration-300"></i>
                </div>
              </button>
            </div>
          </div>

          {/* Bottom Slogan */}
          <div
            className={`absolute bottom-12 left-0 w-full px-6 md:px-16 transition-all duration-1200 delay-500 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="flex items-center gap-6">
              <span className="w-16 h-px bg-white/30"></span>
              <p
                className="text-white/50 text-[1rem] tracking-[0.4em]"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                天地能量 · 守護身心 · 手作溫度 · 精品質感
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 right-10 hidden md:flex flex-col items-center gap-2 z-10">
          <span
            className="text-white/40 text-[0.875rem] tracking-widest"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            SCROLL
          </span>
          <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent animate-pulse"></div>
        </div>
      </section>

      <EnergyQuizModal isOpen={quizOpen} onClose={() => setQuizOpen(false)} />
    </>
  );
}
